import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './WebsiteAIChatbot.css';

const WebsiteAIChatbot = () => {
  const counterRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const animateCounter = () => {
      const counter = counterRef.current;
      if (!counter) return;

      const start = 0;
      const end = 45;
      const duration = 10000;
      let startTime = null;

      const step = (timestamp) => {
        if (startTime === null) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        counter.textContent = Math.floor(progress * (end - start) + start);
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    };

    // Set up intersection observer to trigger animation when element is in view
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter();
            observerRef.current.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    // Start observing the counter element
    const counterElement = counterRef.current;
    if (counterElement) {
      observerRef.current.observe(counterElement);
    }

    // Cleanup function
    return () => {
      if (observerRef.current && counterElement) {
        observerRef.current.unobserve(counterElement);
      }
    };
  }, []); // Empty dependency array means this effect runs once on mount

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Best Website AI Chatbot to Boost Leads and user Engagement</title>
        <meta
          name="description"
          content="Transform your website with an intelligent conversational AI solution for 24/7 lead generation with seamless brand experience."
        />
        <meta
          name="keywords"
          content="Website AI Chatbot, Smart AI Tools, AI Chatbot, Voice-Enabled Chat Assistant, AI Website Assistant, ai chatbot for website"
        />
        <link rel="canonical" href="https://www.aichatassist.com/platform/website-ai-chatbot" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="AI Chatbot for Websites | aichatassist in SG, UK & UAE"
        />
        <meta
          property="og:description"
          content="Get advanced website AI chatbot solutions from aichatassist. Serving Singapore, UK & UAE with smart AI tools to enhance customer interaction and support."
        />
        <meta
          property="og:url"
          content="https://www.aichatassist.com/platform/website-ai-chatbot"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta property="og:site_name" content="AI Chat Assist" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI Chatbot for Websites | aichatassist in SG, UK & UAE"
        />
        <meta
          name="twitter:description"
          content="Get advanced website AI chatbot solutions from aichatassist. Serving Singapore, UK & UAE with smart AI tools to enhance customer interaction and support."
        />
        <meta
          name="twitter:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta name="twitter:site" content="@aichatassist" />
      </Helmet>

      {/* Hero Section - Matching Website Branding */}
      <section id="home" className="py-5 mt-4 position-relative">
        <div className="container text-center">
          <div className="brand-badge mb-3">
            <span className="badge rounded-pill bg-primary-soft px-3 py-2">
              AI-Powered Conversations
            </span>
          </div>

          <h1 className="hero-heading mb-4 animate-up">
            <span className=" position-relative">Website &nbsp;</span>
            <span className="hero-highlight">AI Chatbot</span>
            <br />
            for your <span className="hero-highlight">Business!</span>
          </h1>
          <p
            className="description mx-auto mb-5"
            style={{ maxWidth: '700px', fontSize: '1.1rem', lineHeight: '1.6' }}
          >
            Use a smart AI chatbot for your website to engage users, provide instant support, and
            convert leads 24/7—creating a seamless brand experience.
          </p>

          <div className="metrics-cards" style={{ marginTop: '100px' }}>
            <div className="row g-4">
              <div className="col-md-3">
                <div
                  className="metric-card rounded-4 bg-white shadow-sm h-100"
                  style={{
                    padding: '30px',
                    borderRadius: '20px',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '300px',
                  }}
                >
                  <p className="text-muted mb-5" style={{ fontSize: '18px', fontWeight: '400' }}>
                    Boost support efficiency by 60% with a Website AI Chatbot that automates common
                    queries.
                  </p>
                  <div
                    className="metric-percentage embossed-text"
                    style={{
                      fontSize: '100px',
                      fontWeight: '500',
                      color: '#adb5bd',
                      opacity: '0.5',
                      position: 'absolute',
                      bottom: '-15px',
                      right: '10px',
                    }}
                  >
                    60%
                  </div>
                  <div className="hover-gradient">60%</div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className="metric-card rounded-4 bg-white shadow-sm h-100"
                  style={{
                    padding: '30px',
                    borderRadius: '20px',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '300px',
                  }}
                >
                  <p className="text-muted mb-5" style={{ fontSize: '18px', fontWeight: '400' }}>
                    80% of users engage within the first minute—thanks to smart AI chat prompts.
                  </p>
                  <div
                    className="metric-percentage"
                    style={{
                      fontSize: '100px',
                      fontWeight: '500',
                      position: 'absolute',
                      bottom: '-15px',
                      right: '10px',
                    }}
                  >
                    <span className="gradient-text">80%</span>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className="metric-card rounded-4 bg-white shadow-sm h-100"
                  style={{
                    padding: '30px',
                    borderRadius: '20px',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '300px',
                  }}
                >
                  <p className="text-muted mb-5" style={{ fontSize: '18px', fontWeight: '400' }}>
                    Increase lead conversion by over 45% with AI-driven forms and smart
                    call-to-actions.
                  </p>
                  <div
                    className="metric-percentage embossed-text"
                    style={{
                      fontSize: '100px',
                      fontWeight: '500',
                      color: '#adb5bd',
                      opacity: '0.5',
                      position: 'absolute',
                      bottom: '-15px',
                      right: '10px',
                    }}
                  >
                    45%
                  </div>
                  <div className="hover-gradient">45%</div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className="metric-card rounded-4 bg-white shadow-sm h-100"
                  style={{
                    padding: '30px',
                    borderRadius: '20px',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '300px',
                  }}
                >
                  <p className="text-muted mb-5" style={{ fontSize: '18px', fontWeight: '400' }}>
                    Cut support costs by 50% using an AI chatbot for your website that handles
                    repetitive questions.
                  </p>
                  <div
                    className="metric-percentage embossed-text"
                    style={{
                      fontSize: '100px',
                      fontWeight: '500',
                      color: '#adb5bd',
                      opacity: '0.5',
                      position: 'absolute',
                      bottom: '-15px',
                      right: '10px',
                    }}
                  >
                    50%
                  </div>
                  <div className="hover-gradient">50%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section py-5">
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2 className="mb-3">
              Achieve Your <span className="hero-highlight">Business Goals</span> with
            </h2>
            <h3 className="text-gradient mb-4">Website AI Chatbot & Smart AI Tools</h3>
            <p className="lead mx-auto" style={{ maxWidth: '700px' }}>
              Use Smart AI Tools and a Website AI Chatbot to improve engagement, streamline support,
              and drive conversions—backed by real-time insights.
            </p>
          </div>

          {/* Feature 1 - Image Right */}
          <div className="webchat-feature-section py-5">
            <div className="container">
              <div className="row">
                <div className="col-lg-6 d-flex align-items-center">
                  <div className="webchat-feature-content pe-lg-5">
                    <div className="webchat-feature-header mb-4">
                      <div className="webchat-feature-badge mb-3">
                        <span className="badge rounded-pill bg-primary-soft px-3 py-2">
                          Quick Setup
                        </span>
                      </div>
                      <h3 className="webchat-feature-title">
                        Easy Setup for Your AI Website Assistant
                      </h3>
                    </div>
                    <p className="webchat-feature-description mb-4">
                      Launch your AI Website Assistant in minutes—no coding required. Just copy,
                      paste, and start chatting with your visitors.
                    </p>
                    <ul className="webchat-feature-list list-unstyled">
                      <li className="webchat-feature-item d-flex align-items-center mb-3">
                        <div className="webchat-feature-icon-wrapper">
                          <div className="webchat-feature-icon rounded-circle">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                        <span className="webchat-feature-text">No technical skills required</span>
                      </li>
                      <li className="webchat-feature-item d-flex align-items-center mb-3">
                        <div className="webchat-feature-icon-wrapper">
                          <div className="webchat-feature-icon rounded-circle">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                        <span className="webchat-feature-text">Simple copy-paste installation</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-lg-6 d-flex align-items-center">
                  <div className="webchat-feature-image-container">
                    <img
                      src="/images/easyinstallation.png"
                      alt="Easy One-Click Setup"
                      className="webchat-feature-image img-fluid rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2 - Image Left */}
          <div className="webchat-feature-section py-5">
            <div className="container">
              <div className="row">
                <div className="col-lg-6 order-lg-2 d-flex align-items-center">
                  <div className="webchat-feature-content ps-lg-5">
                    <div className="webchat-feature-header mb-4">
                      <div className="webchat-feature-badge mb-3">
                        <span className="badge rounded-pill bg-primary-soft px-3 py-2">
                          Smart Learning
                        </span>
                      </div>
                      <h3 className="webchat-feature-title">
                        Custom Knowledge for Your Website AI Chatbot
                      </h3>
                    </div>
                    <p className="webchat-feature-description mb-4">
                      Train your Website AI Chatbot to answer business-specific questions. Build a
                      dynamic knowledge base that improves with every interaction.
                    </p>
                    <ul className="webchat-feature-list list-unstyled">
                      <li className="webchat-feature-item d-flex align-items-center mb-3">
                        <div className="webchat-feature-icon-wrapper">
                          <div className="webchat-feature-icon rounded-circle">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                        <span className="webchat-feature-text">Train with your own content</span>
                      </li>
                      <li className="webchat-feature-item d-flex align-items-center mb-3">
                        <div className="webchat-feature-icon-wrapper">
                          <div className="webchat-feature-icon rounded-circle">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                        <span className="webchat-feature-text">
                          Continuously improving responses
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-lg-6 order-lg-1 d-flex align-items-center">
                  <div className="webchat-feature-image-container">
                    <img
                      src="/images/customknowledge.png"
                      alt="Personalized Knowledge Base"
                      className="webchat-feature-image img-fluid rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 3 - Image Right */}
          <div className="webchat-feature-section py-5">
            <div className="container">
              <div className="row">
                <div className="col-lg-6 d-flex align-items-center">
                  <div className="webchat-feature-content pe-lg-5">
                    <div className="webchat-feature-header mb-4">
                      <div className="webchat-feature-badge mb-3">
                        <span className="badge rounded-pill bg-primary-soft px-3 py-2">
                          Brand Identity
                        </span>
                      </div>
                      <h3 className="webchat-feature-title">Align Chat Style with Your Brand</h3>
                    </div>
                    <p className="webchat-feature-description mb-4">
                      Customize your chatbot’s name, look, and tone of voice to match your brand’s
                      identity—creating a consistent, human-like experience with every interaction.
                    </p>
                    <ul className="webchat-feature-list list-unstyled">
                      <li className="webchat-feature-item d-flex align-items-center mb-3">
                        <div className="webchat-feature-icon-wrapper">
                          <div className="webchat-feature-icon rounded-circle">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                        <span className="webchat-feature-text">Custom colors and branding</span>
                      </li>
                      <li className="webchat-feature-item d-flex align-items-center mb-3">
                        <div className="webchat-feature-icon-wrapper">
                          <div className="webchat-feature-icon rounded-circle">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                        <span className="webchat-feature-text">Tone and voice customization</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-lg-6 d-flex align-items-center">
                  <div className="webchat-feature-image-container">
                    <img
                      src="/images/branding.png"
                      alt="Brand & Personality Match"
                      className="webchat-feature-image img-fluid rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 4 - Image Left */}
          <div className="webchat-feature-section py-5">
            <div className="container">
              <div className="row">
                <div className="col-lg-6 order-lg-2 d-flex align-items-center">
                  <div className="webchat-feature-content ps-lg-5">
                    <div className="webchat-feature-header mb-4">
                      <div className="webchat-feature-badge mb-3">
                        <span className="badge rounded-pill bg-primary-soft px-3 py-2">
                          Data Insights
                        </span>
                      </div>
                      <h3 className="webchat-feature-title">
                        Analytics to Optimize Your AI Chatbot
                      </h3>
                    </div>
                    <p className="webchat-feature-description mb-4">
                      Understand how users interact with your AI chatbot using clear reports and
                      behavior data—so you can refine performance and boost results.
                    </p>
                    <ul className="webchat-feature-list list-unstyled">
                      <li className="webchat-feature-item d-flex align-items-center mb-3">
                        <div className="webchat-feature-icon-wrapper">
                          <div className="webchat-feature-icon rounded-circle">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                        <span className="webchat-feature-text">
                          Comprehensive analytics dashboard
                        </span>
                      </li>
                      <li className="webchat-feature-item d-flex align-items-center mb-3">
                        <div className="webchat-feature-icon-wrapper">
                          <div className="webchat-feature-icon rounded-circle">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                        <span className="webchat-feature-text">
                          Conversion tracking and reporting
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-lg-6 order-lg-1 d-flex align-items-center">
                  <div className="webchat-feature-image-container">
                    <img
                      src="/images/visitordata.png"
                      alt="Visitor Insights & Analytics"
                      className="webchat-feature-image img-fluid rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Support Section */}
      <section id="features" className="webchat-support-section py-5">
        <div className="container">
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="webchat-support-header">
                <h2 className="webchat-support-title mb-3">
                  Smarter Support with a Website AI Chatbot
                </h2>
                <p className="webchat-support-description">
                  Add a Website AI Chatbot to your site to answer questions instantly, support users
                  around the clock, and improve the customer experience.
                </p>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {/* Support Card 1 */}
            <div className="col-md-6 col-lg-3">
              <div className="webchat-support-card">
                <div className="webchat-support-card-icon-wrapper">
                  <div className="webchat-support-card-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                  </div>
                </div>
                <h4 className="webchat-support-card-title">Natural Conversations with AI</h4>
                <p className="webchat-support-card-text">
                  Deliver human-like chats with AI that understands user intent, context, and
                  tone—making every interaction more engaging and effective.
                </p>
                <ul className="webchat-support-card-list">
                  <li className="webchat-support-card-list-item">
                    <div className="webchat-support-card-check">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>Natural language processing</span>
                  </li>
                  <li className="webchat-support-card-list-item">
                    <div className="webchat-support-card-check">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>Contextual understanding</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Support Card 2 */}
            <div className="col-md-6 col-lg-3">
              <div className="webchat-support-card">
                <div className="webchat-support-card-icon-wrapper">
                  <div className="webchat-support-card-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                      <path d="M8 14h.01"></path>
                      <path d="M12 14h.01"></path>
                      <path d="M16 14h.01"></path>
                      <path d="M8 18h.01"></path>
                      <path d="M12 18h.01"></path>
                      <path d="M16 18h.01"></path>
                    </svg>
                  </div>
                </div>
                <h4 className="webchat-support-card-title">Always-On Website AI Chatbot</h4>
                <p className="webchat-support-card-text">
                  Be available to customers anytime with a Website AI Chatbot that responds
                  instantly—even when your team is offline.
                </p>
                <ul className="webchat-support-card-list">
                  <li className="webchat-support-card-list-item">
                    <div className="webchat-support-card-check">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>Instant responses</span>
                  </li>
                  <li className="webchat-support-card-list-item">
                    <div className="webchat-support-card-check">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>No human intervention needed</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Support Card 3 */}
            <div className="col-md-6 col-lg-3">
              <div className="webchat-support-card">
                <div className="webchat-support-card-icon-wrapper">
                  <div className="webchat-support-card-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                </div>
                <h4 className="webchat-support-card-title">Personalized AI Website Assistant</h4>
                <p className="webchat-support-card-text">
                  Let your AI Website Assistant adapt to each user, offering more relevant
                  conversations every time they return.
                </p>
                <ul className="webchat-support-card-list">
                  <li className="webchat-support-card-list-item">
                    <div className="webchat-support-card-check">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>User recognition</span>
                  </li>
                  <li className="webchat-support-card-list-item">
                    <div className="webchat-support-card-check">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>Conversation history</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Support Card 4 - Enhanced Easy Integration */}
            <div className="col-md-6 col-lg-3">
              <div
                className="webchat-support-card webchat-easy-integration-card"
                style={{ borderRadius: '16px' }}
              >
                <div className="webchat-easy-integration-badge">No Coding Required</div>
                <div className="webchat-support-card-icon-wrapper">
                  <div className="webchat-support-card-icon webchat-easy-integration-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                      <line x1="8" y1="21" x2="16" y2="21"></line>
                      <line x1="12" y1="17" x2="12" y2="21"></line>
                      <path d="M7 9l3 3 7-7"></path>
                    </svg>
                  </div>
                </div>
                <h4 className="webchat-support-card-title webchat-easy-integration-title">
                  Easy Integration
                </h4>
                <div className="webchat-easy-integration-steps">
                  <div className="webchat-easy-integration-step">
                    <div className="webchat-easy-integration-step-number">1</div>
                    <div className="webchat-easy-integration-step-content">
                      <h5 className="webchat-easy-integration-step-heading">Copy code</h5>
                      <p className="webchat-easy-integration-step-desc">
                        Copy your custom AI chatbot code
                      </p>
                    </div>
                  </div>
                  <div className="webchat-easy-integration-connector"></div>
                  <div className="webchat-easy-integration-step">
                    <div className="webchat-easy-integration-step-number">2</div>
                    <div className="webchat-easy-integration-step-content">
                      <h5 className="webchat-easy-integration-step-heading">Paste to website</h5>
                      <p className="webchat-easy-integration-step-desc">
                        Paste it into your website’s header
                      </p>
                    </div>
                  </div>
                  <div className="webchat-easy-integration-connector"></div>
                  <div className="webchat-easy-integration-step">
                    <div className="webchat-easy-integration-step-number">3</div>
                    <div className="webchat-easy-integration-step-content">
                      <h5 className="webchat-easy-integration-step-heading">Go live!</h5>
                      <p className="webchat-easy-integration-step-desc">
                        Go live and start engaging users instantly
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Efficiency Stats Section */}
      {/* <section className="efficiency-stats-section py-5" style={{
       
        color: '#000',
        borderRadius: '0px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="efficiency-content p-4">
                <div className="efficiency-heading mb-4">
                  <h2 className="mb-0 position-relative">
                    <span className="text-white-50 fs-4" style={{
                      position: 'relative',
                      display: 'inline-block'
                    }}>Up to</span>
                    <span className="display-1 fw-bold text-white" style={{
                      position: 'relative',
                      display: 'inline-block'
                    }}>
                      <span id="efficiency-percentage" className="counter" ref={counterRef}>0</span>%
                    </span>
                  </h2>
                </div>
                <p className="lead text-white-50" style={{
                  fontSize: '1.25rem',
                  lineHeight: '1.8',
                  maxWidth: '90%'
                }}>
                  Increase your customer support efficiency by up to <span className="text-white fw-bold">45%</span>.
                  Our AI chatbots handle routine inquiries, freeing up your team to focus on
                  complex issues that require a human touch.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="efficiency-image p-4">
                <img
                  src="/images/overviewanalytics.png"
                  alt="Analytics Efficiency"
                  className="img-fluid rounded"
                  style={{
                    transform: 'perspective(1000px) rotateY(-5deg) rotateX(5deg)',
                    transition: 'all 0.5s ease',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'perspective(1000px) rotateY(-5deg) rotateX(5deg)';
                  }}
                />
              </div>
            </div>
          </div>
        </div>

       
        <div className="position-absolute" style={{
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 1
        }}></div>
        <div className="position-absolute" style={{
          bottom: '-100px',
          left: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
          zIndex: 1
        }}></div>
      </section> */}

      {/* Integration Section */}
      {/* <section className="webchat-feature-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="webchat-feature-image-container">
                <img
                  src="/images/easyintegration.png"
                  alt="Easy Integration"
                  className="img-fluid rounded shadow-lg webchat-feature-image"
                />
              </div>
            </div>
            <div className="col-lg-6">
              <div className="webchat-feature-content">
                <div className="webchat-feature-badge">
                  <span className="badge rounded-pill bg-primary-soft px-3 py-2">No Coding Required</span>
                </div>
                <h2 className="webchat-feature-title">Easy <span className="highlight">Integration</span></h2>
                <p className="webchat-feature-description">Adding our AI chatbot with analytics to your website is simple and straightforward. Just copy and paste a small code snippet, and you're ready to go.</p>
                <ul className="webchat-feature-list list-unstyled">
                  <li className="webchat-feature-item d-flex align-items-center">
                    <div className="webchat-feature-icon-wrapper">
                      <div className="webchat-feature-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <span className="webchat-feature-text">No coding knowledge required</span>
                  </li>
                  <li className="webchat-feature-item d-flex align-items-center">
                    <div className="webchat-feature-icon-wrapper">
                      <div className="webchat-feature-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <span className="webchat-feature-text">Works with all major website platforms</span>
                  </li>
                  <li className="webchat-feature-item d-flex align-items-center">
                    <div className="webchat-feature-icon-wrapper">
                      <div className="webchat-feature-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <span className="webchat-feature-text">Dedicated WordPress plugin available</span>
                  </li>
                  <li className="webchat-feature-item d-flex align-items-center">
                    <div className="webchat-feature-icon-wrapper">
                      <div className="webchat-feature-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <span className="webchat-feature-text">Shopify app integration</span>
                  </li>
                  <li className="webchat-feature-item d-flex align-items-center">
                    <div className="webchat-feature-icon-wrapper">
                      <div className="webchat-feature-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <span className="webchat-feature-text">API available for custom implementations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Integration Logos Section */}
      {/* <section className="integration-logos-section py-5">
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2 className="mb-3">Seamless <span className="highlight">Integration</span></h2>
            <p className="lead">Connect your AI Chatbot with your existing business systems</p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="integration-logos d-flex flex-wrap justify-content-center align-items-center gap-4">
                <div className="integration-logo p-3 bg-white rounded shadow-sm">
                  <img src="/images/integrations/salesforce.png" alt="Salesforce" height="50" />
                </div>
                <div className="integration-logo p-3 bg-white rounded shadow-sm">
                  <img src="/images/integrations/hubspot.png" alt="HubSpot" height="50" />
                </div>
                <div className="integration-logo p-3 bg-white rounded shadow-sm">
                  <img src="/images/integrations/shopify.png" alt="Shopify" height="50" />
                </div>
                <div className="integration-logo p-3 bg-white rounded shadow-sm">
                  <img src="/images/integrations/zendesk.png" alt="Zendesk" height="50" />
                </div>
                <div className="integration-logo p-3 bg-white rounded shadow-sm">
                  <img src="/images/integrations/zapier.png" alt="Zapier" height="50" />
                </div>
                <div className="integration-logo p-3 bg-white rounded shadow-sm">
                  <img src="/images/integrations/woocommerce.png" alt="WooCommerce" height="50" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* ... */}
    </>
  );
};

export default WebsiteAIChatbot;
